"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  [
    "[&_svg]:duration-300",
    "[&_svg]:h-4",
    "[&_svg]:transition-all",
    "[&_svg]:w-4",
    "border",
    "cursor-pointer",
    "disabled:cursor-not-allowed",
    "duration-300",
    "ease-in-out",
    "focus-visible:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-douglas-fir",
    "font-medium",
    "gap-2",
    "hover:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.16)]",
    "inline-flex",
    "items-center",
    "justify-center",
    "no-underline",
    "hover:no-underline",
    "px-6",
    "py-2",
    "rounded-full",
    "transition-all",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-scheme-action hover:bg-scheme-action-hover focus-visible:bg-scheme-action-hover border-transparent text-scheme-text-on-action disabled:bg-scheme-action/50",
        secondary:
          "bg-system-grayscale-00 text-system-grayscale-70 border-transparent hover:bg-system-grayscale-10 active:bg-system-grayscale-30 active:text-system-grayscale-70 disabled:bg-system-grayscale-10 disabled:text-system-grayscale-30",
        outline:
          "border-scheme-border hover:border-brand-primary-dark text-brand-primary-dark focus-visible:border-[#3B482F]/30 active:border-brand-primary-dark/70 active:text-brand-primary-dark/70 disabled:border-system-grayscale-30 disabled:text-system-grayscale-30 bg-transparent",
        // Quieter sibling of `outline`: a hairline scheme-tokened border that
        // recedes until hovered, for dense admin/portal chrome where a full
        // brand-green outline is too loud. Opt in explicitly — `outline` keeps
        // its marketing weight for the ~100 call sites that rely on it.
        "outline-subtle":
          "border-scheme-border text-brand-primary-dark hover:border-scheme-text-secondary active:text-brand-primary-dark/70 disabled:border-system-grayscale-30 disabled:text-system-grayscale-30 bg-transparent",
        ghost:
          "text-brand-primary-dark active:text-brand-primary-dark/70 border-none bg-transparent outline-none hover:border hover:bg-system-grayscale-00/30 focus-visible:ring-2 disabled:text-system-grayscale-50 disabled:hover:bg-transparent disabled:hover:border-none",
        destructive:
          "bg-system-detrimental-regular text-system-grayscale-00 hover:bg-system-detrimental-dark border-transparent disabled:bg-system-detrimental-light disabled:hover:bg-system-detrimental-light",
        success:
          "bg-system-success-regular text-system-grayscale-00 hover:bg-system-success-dark border-transparent disabled:bg-system-success-light disabled:hover:bg-system-success-light",
        warning:
          "bg-system-warning-regular text-system-grayscale-00 hover:bg-system-warning-dark border-transparent disabled:bg-system-warning-light disabled:hover:bg-system-warning-light",
        zest: "bg-brand-moss-greens-10 text-brand-primary-darker hover:bg-brand-moss-greens-20 active:bg-brand-moss-greens-20 border-transparent disabled:bg-system-grayscale-10 disabled:text-system-grayscale-30",
        "white-outline":
          "border-white text-white bg-transparent hover:bg-white/10 active:bg-white/20 disabled:border-white/40 disabled:text-white/40",
        // Text-only action (e.g. "Read more" on a card): no border, no fill,
        // underline on hover. Padding is removed below so it sits flush.
        link: "border-none bg-transparent text-scheme-action underline-offset-4 hover:underline hover:shadow-none active:text-scheme-action-hover disabled:text-system-grayscale-30",
      },
      size: {
        default: "min-h-10 px-6 py-2 text-base",
        sm: "h-8 px-4 text-sm",
        lg: "h-12 px-8 text-lg",
        icon: "h-10 w-10 p-2",
        dot: "h-2 w-2 p-0",
      },
    },
    compoundVariants: [
      { variant: "link", class: "h-auto min-h-0 rounded-none p-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      onClick,
      variant,
      size,
      asChild = false,
      startIcon,
      endIcon,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        onClick={onClick}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {startIcon && (
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-full [&_svg]:w-full">
                {startIcon}
              </span>
            )}
            {children}
            {endIcon && (
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-full [&_svg]:w-full">
                {endIcon}
              </span>
            )}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
